import { type ContextFormFieldInfo, type FormContextOptions, type FormContextProps } from "@common/contexts/Form";
import { isNullOrUndefined } from "@common/lib/misc";
import { identity, isEqualWith, isNull, isUndefined } from "lodash";
import React from "react";
import { type OnChange, type OnLoading, type Submit } from "./component";

export const getIsEqual = <T extends {}>(defaultValues: Partial<T>, values: Partial<T>): boolean =>
  isEqualWith(defaultValues, values, (a, b) => {
    // if there's no default value for a text input, then if it's changed to an empty string, it's still the same for the user (for us too)
    if ((isNullOrUndefined(a) || a === "") && (isNullOrUndefined(b) || b === "")) return true;
  });

export const useFormContextValue = <T>(
  onChange: OnChange<T>,
  onLoading: OnLoading<T>,
  submit: Submit,
  hasChanges: boolean,
  options: FormContextOptions
): FormContextProps<T> => {
  const formFields = React.useRef<ContextFormFieldInfo<T>[]>([]);

  const patch = React.useCallback((name, ff) => {
    const index = formFields.current!.findIndex((x) => x.name === name);
    if (index < 0) throw new Error(`BUG. FormContext: form field '${name}' isn't registered`);

    console.debug(`FormContext: patch ${name}`);
    formFields.current[index] = {
      ...formFields.current[index],
      ...ff
    };
  }, []);

  const register = React.useCallback((name: Extract<keyof T, string>) => {
    console.debug(`FormContext: register ${name}`);
    formFields.current!.push({
      name,
      scrollIntoView: identity,
      setError: identity,
      validate: () => Promise.resolve(null)
    });
  }, []);

  const unregister = React.useCallback((name) => {
    const index = formFields.current!.findIndex((x) => x.name === name);
    if (index < 0) throw new Error(`BUG. FormContext: form field '${name}' isn't registered`);

    console.debug(`FormContext: unregister ${name}`);
    formFields.current!.splice(index, 1);
  }, []);

  const validate = React.useCallback(async (): Promise<boolean> => {
    const res = await Promise.all(formFields.current.map((x) => x.validate()));
    const firstInvalidIndex = res.findIndex((x) => !isNull(x) && !isUndefined(x) && x !== "");
    firstInvalidIndex >= 0 && formFields.current[firstInvalidIndex].scrollIntoView();
    return firstInvalidIndex === -1;
  }, []);

  const opts = React.useMemo(
    (): FormContextOptions => ({
      requiredMode: options.requiredMode,
      toast: options.toast
    }),
    [options.requiredMode, options.toast]
  );

  return React.useMemo(
    (): FormContextProps<T> => ({
      hasChanges,
      fields: formFields.current,
      onChange,
      onLoading,
      options: opts,
      patch,
      register,
      submit,
      unregister,
      validate
    }),
    [hasChanges, onChange, onLoading, opts, submit]
  );
};

export class GetFinalValuesError extends Error {}
